import { useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import { AudioPlayer } from '@/components/ui/AudioPlayer';
import { Transcript, AiBlock } from '@/components/ai/AiBadge';
import { EmotionPanel } from './emotion/EmotionPanel';
import { SentimentBadge } from './cdrShared';
import { adminApi, type CdrDetail } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';

/**
 * פרטי הקלטה: נגן + הורדה, סיכום AI, ניתוח רגשי ותמלול (עם "תמלל עכשיו").
 * ב-drawer של השיחה מוצג הכל; בכרטיס ההקלטה (showPlayer=false) הנגן כבר
 * בראש הכרטיס, אז מוצגים רק התמלול/הסיכום וקישור ההורדה.
 */
export function RecordingDetail({ detail, loading, onDetailChange, showPlayer = true }: {
  detail: CdrDetail | null;
  loading: boolean;
  onDetailChange: (updater: (d: CdrDetail | null) => CdrDetail | null) => void;
  showPlayer?: boolean;
}) {
  const [transcribing, setTranscribing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  const recordingId = detail?.recording_id ?? null;
  const rec = detail?.recording ?? null;

  async function startTranscribe() {
    if (recordingId == null) return;
    setTranscribing(true);
    try {
      await adminApi.transcribeRecording(recordingId);
      toast('התמלול נכנס לתור — יתעדכן בקרוב', 'ok');
      onDetailChange((d) => (d ? { ...d, transcript_status: 'pending' } : d));
    } catch {
      toast('שגיאה בהפעלת תמלול', 'danger');
    } finally {
      setTranscribing(false);
    }
  }

  async function analyzeEmotions() {
    if (recordingId == null) return;
    setAnalyzing(true);
    try {
      const res = await adminApi.analyzeRecordingEmotions(recordingId);
      if (res.emotions) {
        onDetailChange((d) => (d ? { ...d, emotions: res.emotions } : d));
        toast('הניתוח הרגשי הופק', 'ok');
      } else {
        toast(res.message ?? 'לא ניתן להפיק ניתוח', 'danger');
      }
    } catch {
      toast('שגיאה בניתוח הרגשי', 'danger');
    } finally {
      setAnalyzing(false);
    }
  }

  if (loading) return <div style={{ color: 'var(--muted)', marginTop: 8, fontSize: 'var(--t-sm)' }}>טוען פרטים…</div>;
  if (!detail) return null;

  const hasFile = !!rec?.file_url && rec?.has_file !== false;

  // כותרת לסרגל הנגן הגלובלי (ממשיך לנגן אחרי סגירת ה-drawer/ניווט).
  const call = detail?.call as { id?: number; from_number?: string; to_number?: string; agent_name?: string } | undefined;
  const audioTitle = call?.id ? `שיחה #${call.id}` : 'הקלטת שיחה';
  const audioSubtitle = [
    call?.from_number && call?.to_number ? `${call.from_number} ← ${call.to_number}` : null,
    call?.agent_name,
  ].filter(Boolean).join(' · ') || undefined;

  return (
    <div className="stack" style={{ marginTop: showPlayer ? 8 : 0 }}>
      {/* הקלטה */}
      {hasFile && rec?.file_url ? (
        showPlayer ? (
          <>
            <AudioPlayer src={rec.file_url} duration={rec.duration ?? 0} title={audioTitle} subtitle={audioSubtitle} />
            <div>
              <a href={rec.download_url ?? rec.file_url} download target="_blank" rel="noreferrer" className="btn">הורד הקלטה</a>
            </div>
          </>
        ) : null
      ) : (
        <div style={{ color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--t-sm)' }}>
          <Icon name="warn" size={16} /> הקלטה לא זמינה
        </div>
      )}

      {/* סיכום AI + סנטימנט + נקודות */}
      {detail.ai_summary && (
        <AiBlock title="סיכום AI">
          {detail.ai_sentiment && <div style={{ marginBottom: 6 }}><SentimentBadge value={detail.ai_sentiment} /></div>}
          {detail.ai_summary}
          {detail.ai_highlights && detail.ai_highlights.length > 0 && (
            <ul style={{ margin: '8px 0 0', paddingInlineStart: 18, fontSize: 'var(--t-sm)' }}>
              {detail.ai_highlights.map((h, i) => <li key={i}>{h}</li>)}
            </ul>
          )}
        </AiBlock>
      )}

      {/* ניתוח רגשי */}
      {detail.emotions && (detail.emotions.timeline?.length ?? 0) > 0 && (
        <EmotionPanel emotions={detail.emotions} src={rec?.file_url ?? null} duration={rec?.duration ?? 0} />
      )}
      {detail.emotion_enabled && !detail.emotions && detail.transcript && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>ניתוח רגשי לא הופק לשיחה זו.</span>
          <button type="button" className="btn soft" onClick={analyzeEmotions} disabled={analyzing}>
            <Icon name="ai" size={14} /> {analyzing ? 'מנתח…' : 'נתח רגש'}
          </button>
        </div>
      )}

      {/* תמלול */}
      {detail.transcript ? (
        <Transcript lines={detail.transcript.lines} names={detail.call?.meta?.speaker_names} />
      ) : detail.transcript_status === 'pending' || detail.transcript_status === 'processing' ? (
        <p style={{ color: 'var(--muted)', margin: 0, fontSize: 'var(--t-sm)' }}>התמלול בתהליך… רענן בעוד רגע.</p>
      ) : recordingId != null ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>
            {detail.transcript_status === 'failed' ? 'התמלול נכשל.' : 'אין תמלול.'}
          </span>
          <button type="button" className="btn soft" onClick={startTranscribe} disabled={transcribing}>
            <Icon name="ai" size={14} /> {transcribing ? 'מתמלל…' : 'תמלל עכשיו'}
          </button>
        </div>
      ) : null}

      {!showPlayer && hasFile && rec?.file_url && (
        <div>
          <a href={rec.download_url ?? rec.file_url} download target="_blank" rel="noreferrer" className="btn soft">
            <Icon name="download" size={14} /> הורד הקלטה
          </a>
        </div>
      )}
    </div>
  );
}
